
import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { 
  Activity, 
  Mail,
  AlertTriangle,
  BarChart3,
  MessageCircle
} from 'lucide-react'
import { useEmailAnalytics } from '@/hooks/useEmailAnalytics'
import { ClientBreakdownModal } from './ClientBreakdownModal'

interface LiveActivityFeedProps {
  campaigns: any[]
  loading: boolean
}

export function LiveActivityFeed({ campaigns, loading }: LiveActivityFeedProps) {
  const { data: emailAnalytics, isLoading: analyticsLoading } = useEmailAnalytics()
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedMetric, setSelectedMetric] = useState<'sent' | 'replies' | 'bounces'>('sent')

  const handleMetricClick = (metric: 'sent' | 'replies' | 'bounces') => {
    setSelectedMetric(metric)
    setModalOpen(true)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Activity className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Live Activity Feed</h3>
        <Badge variant="outline" className="text-xs">
          Real-time
        </Badge>
      </div>

      {/* Enhanced Email Analytics Summary */}
      {emailAnalytics && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Email Analytics Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Emails Sent Row */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Emails Sent</span>
              </div>
              <div 
                className="grid grid-cols-5 gap-4 cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors"
                onClick={() => handleMetricClick('sent')}
                title="Click to view client breakdown"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary hover:text-primary/80 transition-colors">
                    {emailAnalytics.today.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Today</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary hover:text-primary/80 transition-colors">
                    {emailAnalytics.yesterday.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Yesterday</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary hover:text-primary/80 transition-colors">
                    {emailAnalytics.last_7_days.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Last 7 Days</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary hover:text-primary/80 transition-colors">
                    {emailAnalytics.last_14_days.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Last 14 Days</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary hover:text-primary/80 transition-colors">
                    {emailAnalytics.last_30_days.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Last 30 Days</div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Replies Row */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-600">Replies</span>
              </div>
              <div 
                className="grid grid-cols-5 gap-4 cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors"
                onClick={() => handleMetricClick('replies')}
                title="Click to view client breakdown"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 hover:text-green-700 transition-colors">
                    {emailAnalytics.today_replies.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Today</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 hover:text-green-700 transition-colors">
                    {emailAnalytics.yesterday_replies.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Yesterday</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 hover:text-green-700 transition-colors">
                    {emailAnalytics.last_7_days_replies.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Last 7 Days</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 hover:text-green-700 transition-colors">
                    {emailAnalytics.last_14_days_replies.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Last 14 Days</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 hover:text-green-700 transition-colors">
                    {emailAnalytics.last_30_days_replies.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Last 30 Days</div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Bounces Row */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <span className="text-sm font-medium text-muted-foreground">Bounces</span>
              </div>
              <div 
                className="grid grid-cols-5 gap-4 cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors"
                onClick={() => handleMetricClick('bounces')}
                title="Click to view client breakdown"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-destructive hover:text-destructive/80 transition-colors">
                    {emailAnalytics.today_bounces.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Today</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-destructive hover:text-destructive/80 transition-colors">
                    {emailAnalytics.yesterday_bounces.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Yesterday</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-destructive hover:text-destructive/80 transition-colors">
                    {emailAnalytics.last_7_days_bounces.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Last 7 Days</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-destructive hover:text-destructive/80 transition-colors">
                    {emailAnalytics.last_14_days_bounces.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Last 14 Days</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-destructive hover:text-destructive/80 transition-colors">
                    {emailAnalytics.last_30_days_bounces.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Last 30 Days</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Client Breakdown Modal */}
      <ClientBreakdownModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        metricType={selectedMetric}
      />
    </div>
  )
}
